import type { Firestore } from 'firebase-admin/firestore'
import { FieldValue } from 'firebase-admin/firestore'
import type { 
  Clan, 
  ClanMember, 
  ClanMessage, 
  ClanPost, 
  ClanComment, 
  ClanInvite,
  UserPresence,
  CreateClanDTO,
  UpdateClanDTO 
} from '@my-roadtrip/types'
import { randomUUID } from 'node:crypto'
import type { FirestoreDoc, FirestoreTransaction } from '../../common/types/firestore.js'
import { createLogger } from '@/common/utils/logger'

const logger = createLogger('ClanRepository')

export class ClanRepository {
  constructor(private readonly firestore: Firestore) {}

  // ========================================
  // CLAN CRUD
  // ========================================

  async createClan(ownerId: string, data: CreateClanDTO, ownerProfile: { displayName: string; photoURL?: string }): Promise<Clan> {
    const clanId = randomUUID()
    const now = new Date()

    const clan: Clan = {
      id: clanId,
      name: data.name,
      description: data.description,
      visibility: data.visibility,
      status: 'active',
      ownerId,
      members: [{
        userId: ownerId,
        displayName: ownerProfile.displayName,
        photoURL: ownerProfile.photoURL,
        role: 'owner',
        joinedAt: now,
        isOnline: false
      }],
      memberCount: 1,
      maxMembers: data.maxMembers || 50,
      createdAt: now,
      updatedAt: now,
      lastActivityAt: now,
      messageCount: 0,
      postCount: 0,
      settings: {
        allowInvites: true,
        requireApproval: data.visibility === 'private',
        allowPosts: true,
        allowChat: true
      }
    }

    await this.firestore.collection('clans').doc(clanId).set(clan)
    return clan
  }

  async getClan(clanId: string): Promise<Clan | null> {
    const doc = await this.firestore.collection('clans').doc(clanId).get()
    return doc.exists ? (doc.data() as Clan) : null
  }

  async updateClan(clanId: string, data: UpdateClanDTO): Promise<void> {
    await this.firestore.collection('clans').doc(clanId).update({
      ...data,
      updatedAt: new Date()
    })
  }

  async deleteClan(clanId: string): Promise<void> {
    await this.firestore.collection('clans').doc(clanId).update({
      status: 'disbanded',
      updatedAt: new Date()
    })
  }

  async getUserClans(userId: string): Promise<Clan[]> {
    try {
      const snapshot = await this.firestore.collection('clans')
        .where('members', 'array-contains', { userId })
        .where('status', '==', 'active')
        .orderBy('lastActivityAt', 'desc')
        .limit(50)
        .get()

      // Primary path: query succeeds
      return snapshot.docs.map((doc: FirestoreDoc) => doc.data() as Clan)
    } catch (error) {
      // Fallback: fetch active clans and filter in-memory (avoids index/object matching issues)
      logger.warn('[ClanRepository] Fallback getUserClans triggered', { error })
      const snapshot = await this.firestore.collection('clans')
        .where('status', '==', 'active')
        .limit(200)
        .get()

      return snapshot.docs
        .map((doc: FirestoreDoc) => doc.data() as Clan)
        .filter((clan: Clan) => clan.members?.some?.(member => member.userId === userId))
    }
  }

  async searchClans(query: string, limit = 20): Promise<Clan[]> {
    // Simple name search (case-insensitive)
    const snapshot = await this.firestore.collection('clans')
      .where('visibility', '==', 'public')
      .where('status', '==', 'active')
      .orderBy('memberCount', 'desc')
      .limit(limit)
      .get()

    const clans = snapshot.docs.map((doc: FirestoreDoc) => doc.data() as Clan)
    
    // Filter by name (client-side - Firestore doesn't support LIKE)
    const lowerQuery = query.toLowerCase()
    return clans.filter((clan: Clan) => 
      clan.name.toLowerCase().includes(lowerQuery) ||
      clan.description?.toLowerCase().includes(lowerQuery)
    )
  }

  // ========================================
  // MEMBER MANAGEMENT
  // ========================================

  async addMember(clanId: string, member: ClanMember): Promise<void> {
    const clanRef = this.firestore.collection('clans').doc(clanId)
    
    await this.firestore.runTransaction(async (transaction: FirestoreTransaction) => {
      const clanDoc = await transaction.get(clanRef)
      if (!clanDoc.exists) throw new Error('Clan not found')

      const clan = clanDoc.data() as Clan
      const members = [...clan.members, member]

      transaction.update(clanRef, {
        members,
        memberCount: members.length,
        updatedAt: new Date()
      })
    })
  }

  async removeMember(clanId: string, userId: string): Promise<void> {
    const clanRef = this.firestore.collection('clans').doc(clanId)
    
    await this.firestore.runTransaction(async (transaction: FirestoreTransaction) => {
      const clanDoc = await transaction.get(clanRef)
      if (!clanDoc.exists) throw new Error('Clan not found')

      const clan = clanDoc.data() as Clan
      const members = clan.members.filter(m => m.userId !== userId)

      transaction.update(clanRef, {
        members,
        memberCount: members.length,
        updatedAt: new Date()
      })
    })
  }

  async updateMemberRole(clanId: string, userId: string, newRole: 'admin' | 'member'): Promise<void> {
    const clanRef = this.firestore.collection('clans').doc(clanId)
    
    await this.firestore.runTransaction(async (transaction: FirestoreTransaction) => {
      const clanDoc = await transaction.get(clanRef)
      if (!clanDoc.exists) throw new Error('Clan not found')

      const clan = clanDoc.data() as Clan
      const members = clan.members.map(m => 
        m.userId === userId ? { ...m, role: newRole } : m
      )

      transaction.update(clanRef, {
        members,
        updatedAt: new Date()
      })
    })
  }

  // ========================================
  // CHAT MESSAGES
  // ========================================

  async createMessage(message: Omit<ClanMessage, 'id'>): Promise<ClanMessage> {
    const messageId = randomUUID()
    const fullMessage: ClanMessage = { ...message, id: messageId }

    await this.firestore
      .collection('clans')
      .doc(message.clanId)
      .collection('messages')
      .doc(messageId)
      .set(fullMessage)

    // Update clan message count
    await this.firestore.collection('clans').doc(message.clanId).update({
      messageCount: FieldValue.increment(1),
      lastActivityAt: new Date()
    })

    return fullMessage
  }

  async getMessages(clanId: string, limit = 50, startAfter?: FirestoreDoc): Promise<ClanMessage[]> {
    let query = this.firestore
      .collection('clans')
      .doc(clanId)
      .collection('messages')
      .orderBy('timestamp', 'desc')
      .limit(limit)

    if (startAfter) {
      query = query.startAfter(startAfter)
    }

    const snapshot = await query.get()
    return snapshot.docs.map((doc: FirestoreDoc) => doc.data() as ClanMessage)
  }

  async deleteMessage(clanId: string, messageId: string): Promise<void> {
    await this.firestore
      .collection('clans')
      .doc(clanId)
      .collection('messages')
      .doc(messageId)
      .update({
        deletedAt: new Date()
      })
  }

  // ========================================
  // FEED POSTS
  // ========================================

  async createPost(post: Omit<ClanPost, 'id'>): Promise<ClanPost> {
    const postId = randomUUID()
    const fullPost: ClanPost = { 
      ...post, 
      id: postId,
      likeCount: 0,
      commentCount: 0,
      likedBy: [],
      isPinned: false
    }

    await this.firestore
      .collection('clans')
      .doc(post.clanId)
      .collection('posts')
      .doc(postId)
      .set(fullPost)

    // Update clan post count
    await this.firestore.collection('clans').doc(post.clanId).update({
      postCount: FieldValue.increment(1),
      lastActivityAt: new Date()
    })

    return fullPost
  }

  async getPosts(clanId: string, limit = 20, startAfter?: FirestoreDoc): Promise<ClanPost[]> {
    let query = this.firestore
      .collection('clans')
      .doc(clanId)
      .collection('posts')
      .where('deletedAt', '==', null)
      .orderBy('isPinned', 'desc')
      .orderBy('createdAt', 'desc')
      .limit(limit)

    if (startAfter) {
      query = query.startAfter(startAfter)
    }

    const snapshot = await query.get()
    return snapshot.docs.map((doc: FirestoreDoc) => doc.data() as ClanPost)
  }

  async getPost(clanId: string, postId: string): Promise<ClanPost | null> {
    const doc = await this.firestore
      .collection('clans')
      .doc(clanId)
      .collection('posts')
      .doc(postId)
      .get()

    return doc.exists ? (doc.data() as ClanPost) : null
  }

  async likePost(clanId: string, postId: string, userId: string): Promise<void> {
    const postRef = this.firestore
      .collection('clans')
      .doc(clanId)
      .collection('posts')
      .doc(postId)

    await this.firestore.runTransaction(async (transaction: FirestoreTransaction) => {
      const postDoc = await transaction.get(postRef)
      if (!postDoc.exists) throw new Error('Post not found')

      const post = postDoc.data() as ClanPost
      const likedBy = post.likedBy.includes(userId)
        ? post.likedBy.filter(id => id !== userId)
        : [...post.likedBy, userId]

      transaction.update(postRef, {
        likedBy,
        likeCount: likedBy.length,
        updatedAt: new Date()
      })
    })
  }

  async deletePost(clanId: string, postId: string): Promise<void> {
    await this.firestore
      .collection('clans')
      .doc(clanId)
      .collection('posts')
      .doc(postId)
      .update({
        deletedAt: new Date()
      })
  }

  // ========================================
  // COMMENTS
  // ========================================

  async createComment(clanId: string, postId: string, comment: Omit<ClanComment, 'id'>): Promise<ClanComment> {
    const commentId = randomUUID()
    const fullComment: ClanComment = { 
      ...comment, 
      id: commentId,
      likeCount: 0,
      likedBy: []
    }

    await this.firestore
      .collection('clans')
      .doc(clanId)
      .collection('posts')
      .doc(postId)
      .collection('comments')
      .doc(commentId)
      .set(fullComment)

    // Update post comment count
    await this.firestore
      .collection('clans')
      .doc(clanId)
      .collection('posts')
      .doc(postId)
      .update({
        commentCount: FieldValue.increment(1),
        updatedAt: new Date()
      })

    return fullComment
  }

  async getComments(clanId: string, postId: string, limit = 50): Promise<ClanComment[]> {
    const snapshot = await this.firestore
      .collection('clans')
      .doc(clanId)
      .collection('posts')
      .doc(postId)
      .collection('comments')
      .where('deletedAt', '==', null)
      .orderBy('createdAt', 'asc')
      .limit(limit)
      .get()

    return snapshot.docs.map((doc: FirestoreDoc) => doc.data() as ClanComment)
  }

  async deleteComment(clanId: string, postId: string, commentId: string): Promise<void> {
    await this.firestore
      .collection('clans')
      .doc(clanId)
      .collection('posts')
      .doc(postId)
      .collection('comments')
      .doc(commentId)
      .update({
        deletedAt: new Date()
      })
  }

  // ========================================
  // INVITES
  // ========================================

  async createInvite(invite: Omit<ClanInvite, 'id'>): Promise<ClanInvite> {
    const inviteId = randomUUID()
    const fullInvite: ClanInvite = { ...invite, id: inviteId }

    await this.firestore
      .collection('clanInvites')
      .doc(inviteId)
      .set(fullInvite)

    return fullInvite
  }

  async getInvite(inviteId: string): Promise<ClanInvite | null> {
    const doc = await this.firestore.collection('clanInvites').doc(inviteId).get()
    return doc.exists ? (doc.data() as ClanInvite) : null
  }

  async updateInviteStatus(inviteId: string, status: 'accepted' | 'declined'): Promise<void> {
    await this.firestore.collection('clanInvites').doc(inviteId).update({
      status,
      acceptedAt: status === 'accepted' ? new Date() : null
    })
  }

  async getUserInvites(userId: string): Promise<ClanInvite[]> {
    const snapshot = await this.firestore
      .collection('clanInvites')
      .where('inviteeId', '==', userId)
      .where('status', '==', 'pending')
      .orderBy('createdAt', 'desc')
      .limit(20)
      .get()

    return snapshot.docs.map((doc: FirestoreDoc) => doc.data() as ClanInvite)
  }

  // ========================================
  // PRESENCE
  // ========================================

  async updatePresence(presence: UserPresence): Promise<void> {
    await this.firestore
      .collection('clans')
      .doc(presence.clanId)
      .collection('presence')
      .doc(presence.userId)
      .set(presence, { merge: true })
  }

  async getPresence(clanId: string): Promise<UserPresence[]> {
    const snapshot = await this.firestore
      .collection('clans')
      .doc(clanId)
      .collection('presence')
      .where('status', '!=', 'offline')
      .get()

    return snapshot.docs.map((doc: FirestoreDoc) => doc.data() as UserPresence)
  }
}
